/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Sagas for Wizard component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.2.0
 */

import { createElement } from 'react';

import omit from 'lodash/omit';
import set from 'lodash/set';
import { SagaIterator, Task } from 'redux-saga';
import { call, cancel, fork, put, select, take, takeEvery } from 'redux-saga/effects';

import { requestSaga } from '../../sagas/requestSaga';
import { stringFormat } from '../../utils/stringFormat';
import {
    API_HOSTNAME,
    WIZARD,
    WIZARD_BACK,
    WIZARD_FINISH,
    WIZARD_META,
    WIZARD_NEXT,
    WIZARD_START_FROM,
} from '../app/Api';
import { HttpStatus } from '../app/appConstants';
import { breadCrumbsGoBackAction } from '../breadCrumbs/breadCrumbsActions';
import { makeSelectBreadCrumbsData } from '../breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '../breadCrumbs/utils/breadCrumbsGoBack';
import { modalOpen } from '../modal/modalActions';
import { IModalRenderProps } from '../modal/types/modalTypes';
import { setGuardComponentKey } from '../role/actions/guardActions';
import { getItemFromLocalStorage, removeItemLocalStorage, setItemToLocalStorage } from '../storage/storageUtils';
import WizardModalSummary from './components/WizardModalSummary';
import WizardModalTimeout from './components/WizardModalTimeout';
import { wizardReportSagas } from './components/WizardReport/wizardReportSagas';
import {
    wizardBackFailed,
    wizardBackSuccess,
    wizardFinishAction,
    wizardLoaderStartAction,
    wizardLoaderStopAction,
    wizardMetaFailed,
    wizardMetaSuccess,
    wizardNextFailed,
    wizardNextSuccess,
    wizardRestoreFailed,
    wizardRestoreSuccess,
    wizardStartAction,
    wizardStartFailed,
    wizardStartSuccess,
    wizardStorageClearStepsSuccess,
} from './wizardActions';
import {
    EWizardName,
    MapWizardNameToGuardComponentKey,
    WIZARD_BACK_ACTION,
    WIZARD_CANCEL_ACTION,
    WIZARD_FINISH_ACTION,
    WIZARD_INIT_CURRENT_SAGA,
    WIZARD_META_ACTION,
    WIZARD_NEXT_ACTION,
    WIZARD_OPEN_MODAL_SUMMARY,
    WIZARD_RESTORE_ACTION,
    WIZARD_START_ACTION,
    WIZARD_STORAGE_CLEAR_STEPS,
    WIZARD_STORAGE_SAVE_STEP,
    WIZARD_STORAGE_UPDATE_STEP,
} from './wizardConstants';
import {
    makeSelectWizardBasketSnapshotHeader,
    makeSelectWizardBasketSnapshotId,
    makeSelectWizardBasketSnapshotType,
    makeSelectWizardBasketType,
    makeSelectWizardCurrentAccount,
    makeSelectWizardIsRequestsSticky,
    makeSelectWizardName,
} from './wizardSelectors';
import {
    mapWizardNameToSaga,
    TWizardNextAction,
    TWizardOpenModalSummaryAction,
    TWizardRestoreAction,
    TWizardStartAction,
    TWizardStorageClearStepsAction,
    TWizardStorageSaveStepAction,
    TWizardStorageUpdateStepAction,
} from './wizardTypes';

export function* wizardStartSaga(action: TWizardStartAction): SagaIterator {
    try {
        yield put(wizardLoaderStartAction());

        const isRequestsSticky: boolean = yield select(makeSelectWizardIsRequestsSticky());
        const wizardName = yield select(makeSelectWizardName());
        const from = yield select(makeSelectWizardBasketType());

        const selectedAccount = yield select(makeSelectWizardCurrentAccount());
        const selectedAccountUrl = selectedAccount ? `/${selectedAccount}` : '';

        let url =
            API_HOSTNAME +
            stringFormat(WIZARD, wizardName) +
            stringFormat(WIZARD_START_FROM, from) +
            selectedAccountUrl;

        const onlyMegaPowers = action.payload?.data?.onlyMegaPowers;

        if (onlyMegaPowers) {
            const queryString = new URLSearchParams({ onlyMegaPowers: 'true' }).toString();
            url += '?' + queryString;
        }

        const options: any = {};
        if (isRequestsSticky) set(options, 'headers.X-NODE', 'sticky');
        const data = yield call(requestSaga, url, 'POST', action.payload?.data, options);

        // read consignment data
        yield put(wizardStartSuccess(data));
        // read meta step
        yield put(wizardMetaSuccess(data.meta));
    } catch (error: any) {
        yield put(wizardStartFailed(error));
        if (error.code === HttpStatus.NOT_FOUND) {
            const wizardName: EWizardName = yield select(makeSelectWizardName());
            yield put(
                setGuardComponentKey({
                    componentKey: MapWizardNameToGuardComponentKey[wizardName],
                    isEnabled: false,
                }),
            );
        }
    } finally {
        yield put(wizardLoaderStopAction());
    }
}

export function* wizardRestoreSaga(action: TWizardRestoreAction): SagaIterator {
    try {
        yield put(wizardLoaderStartAction());

        const isRequestsSticky: boolean = yield select(makeSelectWizardIsRequestsSticky());
        const wizardName = action.payload.name;
        const basketSnapshotId = action.payload.basketSnapshotId;
        const basketSnapshotType = action.payload.basketSnapshotType;
        const storageStepsData = action.payload.storageStepsData;

        if (!wizardName || !basketSnapshotId || !basketSnapshotType) {
            throw Error('Wrong data for restore');
        }

        const url = API_HOSTNAME + stringFormat(WIZARD, wizardName) + WIZARD_META;
        const header = `${basketSnapshotType}:${basketSnapshotId}`;
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'X-BASKET-SNAPSHOT': header,
            },
        };
        if (isRequestsSticky) set(options, 'headers.X-NODE', 'sticky');
        const data = yield call(requestSaga, url, 'GET', null, options);

        yield put(
            wizardRestoreSuccess({
                basketSnapshotId,
                basketSnapshotType,
                storageStepsData,
            }),
        );
        yield put(wizardMetaSuccess(data));
    } catch (error: any) {
        yield put(wizardRestoreFailed(error));
        if (error.code === HttpStatus.NOT_ACCEPTABLE) {
            return;
        }
        if (error.code === HttpStatus.NOT_FOUND) {
            const wizardName = action.payload.name;
            yield put(
                setGuardComponentKey({
                    componentKey: MapWizardNameToGuardComponentKey[wizardName],
                    isEnabled: false,
                }),
            );
            return;
        }
        // Start a new wizard
        yield put(
            wizardStartAction({
                basketType: action.payload.basketType,
                name: action.payload.name,
                data: action.payload.data,
            }),
        );
    } finally {
        yield put(wizardLoaderStopAction());
    }
}

export function* wizardNextSaga(action: TWizardNextAction): SagaIterator {
    try {
        yield put(wizardLoaderStartAction());

        const isRequestsSticky: boolean = yield select(makeSelectWizardIsRequestsSticky());
        const wizardName = yield select(makeSelectWizardName());
        const url = API_HOSTNAME + stringFormat(WIZARD, wizardName) + WIZARD_NEXT;
        const header = yield select(makeSelectWizardBasketSnapshotHeader());
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'X-BASKET-SNAPSHOT': header,
            },
        };
        if (action.payload?.options?.isSticky || isRequestsSticky) set(options, 'headers.X-NODE', 'sticky');
        const payload = omit({ ...action.payload }, 'options');
        const data = yield call(requestSaga, url, 'POST', payload, options as any);
        yield put(wizardNextSuccess(data));

        // Check next step
        if (data.nextStep) {
            yield put(wizardMetaSuccess(data.nextStep));
        } else {
            yield put(wizardFinishAction());
        }
    } catch (error) {
        yield put(wizardNextFailed(error));
    } finally {
        yield put(wizardLoaderStopAction());
    }
}

export function* wizardBackSaga(): SagaIterator {
    try {
        yield put(wizardLoaderStartAction());

        const isRequestsSticky: boolean = yield select(makeSelectWizardIsRequestsSticky());
        const wizardName = yield select(makeSelectWizardName());
        const url = API_HOSTNAME + stringFormat(WIZARD, wizardName) + WIZARD_BACK;
        const header = yield select(makeSelectWizardBasketSnapshotHeader());
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'X-BASKET-SNAPSHOT': header,
            },
        };
        if (isRequestsSticky) set(options, 'headers.X-NODE', 'sticky');
        const data = yield call(requestSaga, url, 'POST', null, options);

        yield put(wizardBackSuccess(data));
        yield put(wizardMetaSuccess(data.backStep));
    } catch (error) {
        yield put(wizardBackFailed(error));
    } finally {
        yield put(wizardLoaderStopAction());
    }
}

export function* wizardModalTimeoutSaga(): SagaIterator {
    const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
    const goBack = () => breadCrumbGoBack(breadCrumbsData);

    yield put(wizardFinishAction());
    yield put(
        modalOpen({
            onCrossClickCallback: goBack,
            onCloseCallback: goBack,
            render: (props: IModalRenderProps) =>
                createElement(WizardModalTimeout, {
                    dataName: 'wizard-modal-timeout',
                    onCloseModal: props.onCloseModal,
                }),
        }),
    );
}

export function* wizardMetaSaga(): SagaIterator {
    try {
        const isRequestsSticky: boolean = yield select(makeSelectWizardIsRequestsSticky());
        const wizardName = yield select(makeSelectWizardName());
        const url = API_HOSTNAME + stringFormat(WIZARD, wizardName) + WIZARD_META;
        const header = yield select(makeSelectWizardBasketSnapshotHeader());
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'X-BASKET-SNAPSHOT': header,
            },
        };
        if (isRequestsSticky) set(options, 'headers.X-NODE', 'sticky');
        const data = yield call(requestSaga, url, 'GET', null, options as any);

        yield put(wizardMetaSuccess(data));
    } catch (error) {
        yield put(wizardMetaFailed(error));
    }
}

export function* wizardFinishSaga(): SagaIterator {
    try {
        yield put(wizardLoaderStartAction());

        const isRequestsSticky: boolean = yield select(makeSelectWizardIsRequestsSticky());
        const wizardName = yield select(makeSelectWizardName());
        const url = API_HOSTNAME + stringFormat(WIZARD, wizardName) + WIZARD_FINISH;
        const header = yield select(makeSelectWizardBasketSnapshotHeader());
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'X-BASKET-SNAPSHOT': header,
            },
        };
        if (isRequestsSticky) set(options, 'headers.X-NODE', 'sticky');
        yield call(requestSaga, url, 'POST', null, options as any);
        yield call(wizardStorageClearStepsSaga);
    } catch (err) {
        console.error(err);
    } finally {
        yield put(wizardLoaderStopAction());
    }
}

export function* wizardCancelSaga(): SagaIterator {
    try {
        yield call(wizardFinishSaga);
        yield put(breadCrumbsGoBackAction());
    } catch (err) {
        console.error(err);
    }
}

export function* wizardStorageLoadStepsSaga(): SagaIterator {
    try {
        const wizardName = yield select(makeSelectWizardName());
        const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
        const wizardSteps = yield call(getItemFromLocalStorage, wizardName);
        if (wizardSteps[basketSnapshotId]) {
            return wizardSteps;
        } else {
            return {};
        }
    } catch (error) {
        return {};
    }
}

export function* wizardStorageSaveStepSaga(action: TWizardStorageSaveStepAction): SagaIterator {
    const wizardName = yield select(makeSelectWizardName());
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const basketSnapshotType = yield select(makeSelectWizardBasketSnapshotType());
    if (!wizardName || !basketSnapshotId) {
        return;
    }
    const wizardSteps = yield call(wizardStorageLoadStepsSaga);
    set(wizardSteps, `${basketSnapshotId}`, action.payload.params);
    set(wizardSteps, `${basketSnapshotId}.basketSnapshotType`, basketSnapshotType);

    yield call(setItemToLocalStorage, wizardName, JSON.stringify(wizardSteps));
}

export function* wizardStorageUpdateStepSaga(action: TWizardStorageUpdateStepAction): SagaIterator {
    const wizardName = yield select(makeSelectWizardName());
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    if (!wizardName || !basketSnapshotId) {
        return;
    }
    const wizardSteps = yield call(wizardStorageLoadStepsSaga);
    set(wizardSteps, `${basketSnapshotId}`, { ...wizardSteps[basketSnapshotId], ...action.payload });

    yield call(setItemToLocalStorage, wizardName, JSON.stringify(wizardSteps));
}

export function* wizardStorageClearStepsSaga(action?: TWizardStorageClearStepsAction): SagaIterator {
    try {
        const wizardName = action?.payload.wizardName
            ? action.payload.wizardName
            : yield select(makeSelectWizardName());
        yield call(removeItemLocalStorage, wizardName);
    } catch (error) {
        return {};
    } finally {
        yield put(wizardStorageClearStepsSuccess());
    }
}

export function* wizardOpenModalSummarySaga(action: TWizardOpenModalSummaryAction): SagaIterator {
    const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
    const goBack = () => breadCrumbGoBack(breadCrumbsData);
    const orderId = action.payload.orderId;
    const orderType = action.payload.orderType;
    yield put(
        modalOpen({
            onCrossClickCallback: goBack,
            render: (props: IModalRenderProps) =>
                createElement(WizardModalSummary, {
                    dataName: 'wizard-modal-summary',
                    onCloseButtonClick: () => {
                        props.onCloseModal();
                        goBack();
                    },
                    orderId: orderId,
                    orderType: orderType,
                    onCloseModal: props.onCloseModal,
                }),
            modalParams: {
                closeable: false,
            },
        }),
    );
}

export function* wizardInitCurrentSaga(): SagaIterator {
    let currentWizardTask: Task | undefined;
    let currentWizardName: EWizardName;
    let currentSaga: () => SagaIterator | undefined;
    while (true) {
        currentWizardName = (yield take(WIZARD_INIT_CURRENT_SAGA)).payload;
        if (currentWizardTask) yield cancel(currentWizardTask);
        currentSaga = mapWizardNameToSaga[currentWizardName];
        currentWizardTask = currentSaga ? yield fork(currentSaga) : undefined;
    }
}

export function* wizardSagas(): SagaIterator {
    yield fork(wizardInitCurrentSaga);
    yield fork(wizardReportSagas);
    yield takeEvery(WIZARD_START_ACTION, wizardStartSaga);
    yield takeEvery(WIZARD_RESTORE_ACTION, wizardRestoreSaga);
    yield takeEvery(WIZARD_NEXT_ACTION, wizardNextSaga);
    yield takeEvery(WIZARD_BACK_ACTION, wizardBackSaga);
    yield takeEvery(WIZARD_META_ACTION, wizardMetaSaga);
    yield takeEvery(WIZARD_CANCEL_ACTION, wizardCancelSaga);
    yield takeEvery(WIZARD_FINISH_ACTION, wizardFinishSaga);
    yield takeEvery(WIZARD_STORAGE_SAVE_STEP, wizardStorageSaveStepSaga);
    yield takeEvery(WIZARD_STORAGE_UPDATE_STEP, wizardStorageUpdateStepSaga);
    yield takeEvery(WIZARD_OPEN_MODAL_SUMMARY, wizardOpenModalSummarySaga);
    yield takeEvery(WIZARD_STORAGE_CLEAR_STEPS, wizardStorageClearStepsSaga);
}
